/**
 * Gliederung: Verbund › Verwaltungsgemeinschaft › Gemeinde (Verwaltung › Gliederung).
 *
 * Nach der Anmeldung kommt der Baum mit Zahlen und den eigenen Rechten von
 * `/api/catalog/organisations`; oeffentlich (ohne /api) von `/oeffentlich/gliederung`.
 * Ein Einzelkunde hat nur die Wurzel: dann gibt es keinen Umschalter und keine Zuordnung zu sehen.
 */
import { useSyncExternalStore } from 'react';

export type OrganisationKind = 'verbund' | 'vg' | 'gemeinde' | 'sonstige';

export const KIND_LABEL: Record<OrganisationKind, string> = {
  verbund: 'Verbund',
  vg: 'Verwaltungsgemeinschaft',
  gemeinde: 'Gemeinde',
  sonstige: 'Sonstige',
};

export interface OrganisationNode {
  id: string;
  parent_id: string | null;
  kind: OrganisationKind;
  name: string;
  slug: string;
  /** 1 = oberste Ebene. */
  depth: number;
  homepage: string | null;
  hero: { credit: string | null; version: string } | null;
  /** Gemeindeschluessel der Gebiete, die an diesem Eintrag liegen. */
  ags: string[];
  /** Impressum/Datenschutz, bei fehlender eigener Angabe vom Eintrag darueber. */
  imprint_url: string | null;
  privacy_url: string | null;
  /** Aktive Datensaetze: eigene und mit allen Eintraegen darunter. */
  datasets: { own: number; total: number };
  // nur angemeldet:
  email?: string | null;
  own_imprint_url?: string | null;
  own_privacy_url?: string | null;
  can_write?: boolean;
  can_manage?: boolean;
}

export interface OrganisationsState {
  items: OrganisationNode[];
  /** Startpunkt nach der Anmeldung; Knoten, an denen ich schreiben darf. */
  my: { home: string | null; writable: string[]; admin: boolean };
  loaded: boolean;
}

const EMPTY: OrganisationsState = { items: [], my: { home: null, writable: [], admin: false }, loaded: false };

let state: OrganisationsState = EMPTY;
let version = 0;
const listeners = new Set<() => void>();

export function setOrganisations(next: OrganisationsState): void {
  state = next;
  version += 1;
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useOrganisations(): OrganisationsState {
  useSyncExternalStore(subscribe, () => version);
  return state;
}

export function organisationsState(): OrganisationsState {
  return state;
}

/** Oeffentlicher Stand (ohne Anmeldung, ohne /api). */
export async function loadPublicOrganisations(fetchImpl: typeof fetch = fetch): Promise<OrganisationNode[]> {
  const res = await fetchImpl('/oeffentlich/gliederung', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(String(res.status));
  return ((await res.json()) as { items: OrganisationNode[] }).items;
}

/** Gibt es eine Gliederung (mehr als die oberste Ebene)? Sonst bleibt alles wie beim Einzelkunden. */
export function hasStructure(items: OrganisationNode[]): boolean {
  return items.length > 1;
}

/**
 * Darf ich am Eintrag schreiben (ohne Eintrag: oberste Ebene)? Solange der Baum nicht geladen ist:
 * ja, damit nichts flackert; der Server prueft ohnehin und meldet 403 mit Begruendung.
 */
export function canWriteAt(s: OrganisationsState, id: string | null | undefined): boolean {
  if (!s.loaded || s.my.admin) return true;
  const target = id ?? rootOf(s.items)?.id;
  return !!target && s.my.writable.includes(target);
}

export function useCanWrite(id: string | null | undefined): boolean {
  return canWriteAt(useOrganisations(), id);
}

export function rootOf(items: OrganisationNode[]): OrganisationNode | undefined {
  return items.find((o) => o.parent_id === null);
}

export function childrenOf(items: OrganisationNode[], id: string): OrganisationNode[] {
  return items.filter((o) => o.parent_id === id);
}

/** Weg von der obersten Ebene bis zum Eintrag. */
export function pathTo(items: OrganisationNode[], id: string): OrganisationNode[] {
  const byId = new Map(items.map((o) => [o.id, o]));
  const out: OrganisationNode[] = [];
  let n = byId.get(id);
  while (n) {
    out.unshift(n);
    n = n.parent_id ? byId.get(n.parent_id) : undefined;
  }
  return out;
}

/** Der Eintrag und alle darunter (Ids). */
export function subtreeOf(items: OrganisationNode[], id: string): string[] {
  const out = [id];
  for (const c of childrenOf(items, id)) out.push(...subtreeOf(items, c.id));
  return out;
}

/** Kurzform fuer Pfad und Kacheln: „Gemeinde Unterdießen“ → „Unterdießen“. */
export function shortName(o: Pick<OrganisationNode, 'name'>): string {
  return o.name.replace(/^(Gemeinde|Markt|Stadt|Verwaltungsgemeinschaft)\s+/u, '');
}

/** Schild einer Verwaltungsgemeinschaft auf den Kacheln: immer „VG Fuchstal“, auch wenn sie so heisst. */
export function vgLabel(o: Pick<OrganisationNode, 'name'>): string {
  return /^VG\s/u.test(o.name) ? o.name : `VG ${shortName(o)}`;
}

/** Titelbild-Adresse (mit Version gegen veraltete Caches) oder null. */
export function heroUrl(o: Pick<OrganisationNode, 'slug' | 'hero'>): string | null {
  return o.hero ? `/oeffentlich/titelbild/${o.slug}?v=${encodeURIComponent(o.hero.version)}` : null;
}

const AMT_PREFIX = /^(gemeinde|markt|stadt|verwaltungsgemeinschaft|vg|landkreis|zweckverband)\s+/iu;

/** Vorschlag fuer die Adresse aus dem Namen (wie im Katalog): „Gemeinde Unterdießen“ → „unterdiessen“. */
export function slugify(name: string): string {
  return name
    .trim()
    .replace(AMT_PREFIX, '')
    .toLowerCase()
    .replace(/ä/gu, 'ae')
    .replace(/ö/gu, 'oe')
    .replace(/ü/gu, 'ue')
    .replace(/ß/gu, 'ss')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/gu, '')
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/^-+|-+$/gu, '')
    .slice(0, 63)
    .replace(/-+$/u, '');
}

/** Laedt den Baum nach der Anmeldung neu (Verwaltung, Umschalter, Uebersicht). */
export async function refreshOrganisations(list: () => Promise<Pick<OrganisationsState, 'items' | 'my'>>): Promise<OrganisationsState> {
  const res = await list();
  setOrganisations({ ...res, loaded: true });
  return organisationsState();
}
